'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Transition } from 'framer-motion';
import { easeInOut } from 'framer-motion';
import { PanelTopClose, PanelTopOpen, Trash } from 'lucide-react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useBoardEditorSaveFile } from '@/hooks/feature/boards/useBoardEditorSaveFile';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBoardEditorConfigStore } from '@/stores/useBoardEditorConfigStore';
import { useBoardEditorFileStore } from '@/stores/useBoardEditorFileStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { useModalSlideStore } from '@/stores/useModalSlideStore';
import IconAdaptive from '@/components/IconAdaptive';
import { getInitialCode, getInitialXml } from '@/(routers)/boards/editor/block/blockConfig';
import BoardCodeFileList from '@/(routers)/boards/editor/components/modal/BoardCodeFileList';
import BoardAIAssistant from '@/(routers)/boards/editor/components/unit/BoardAIAssistant';
import BoardBlockEditor from '@/(routers)/boards/editor/components/unit/BoardBlockEditor';
import BoardCodeEditor from '@/(routers)/boards/editor/components/unit/BoardCodeEditor';
import BoardEditorHeader from '@/(routers)/boards/editor/components/unit/BoardEditorHeader';
import BoardEditorSidebar from '@/(routers)/boards/editor/components/unit/BoardEditorSidebar';
import BoardLogConsole from '@/(routers)/boards/editor/components/unit/BoardLogConsole';
import BoardToolbar from '@/(routers)/boards/editor/components/unit/BoardToolbar';
import ProgressOverlay from '@/(routers)/boards/editor/components/unit/ProgressOverlay';

const MIN_PANEL_HEIGHT = 100;
const SEPARATOR_HEIGHT = 34;
const DEFAULT_TOP_RATIO = 0.9;
const DRAG_TRANSITION_DURATION = 0;
const EXPAND_TRANSITION_DURATION = 0.4;

export default function BoardSplitEditor() {
    const containerRef = useRef<HTMLDivElement>(null);
    const preferredRatioRef = useRef(DEFAULT_TOP_RATIO);
    const startY = useRef(0);
    const startTop = useRef(0);
    const [topPx, setTopPx] = useState(480);
    const [dragging, setDragging] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [totalHeight, setTotalHeight] = useState(0);
    const { openModalSlide, closeModalSlide } = useModalSlideStore();
    const { isDark, isAIWhiteEnabled, hasHydrated } = useBoardEditorConfigStore();
    const {
        isUploading,
        uploadProgress,
        uploadStatus,
        uploadMessage,
        uploadQueueAhead,
        activeCompileJobId,
        logs,
        clearLogs,
        editorMode,
        setCode,
    } = useBoardEditorStore();
    const { addFile, setCurrentFile } = useBoardEditorFileStore();
    const initializedRef = useRef(false);
    const sessionLoggedRef = useRef(false);
    const expandedRef = useRef(expanded);

    useEffect(() => {
        expandedRef.current = expanded;
    }, [expanded]);

    useEffect(() => {
        if (sessionLoggedRef.current) return;
        sessionLoggedRef.current = true;
        apiFetch('/boards/editor/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        }).catch(() => {
            // ログ失敗はサイレントに無視
        });
    }, []);

    const handleOpenFileList = () => {
        openModalSlide({
            id: 'board-code-file-list',
            component: <BoardCodeFileList onClose={() => closeModalSlide()} />,
            size: { pcWidth: '900px', mbWidth: '100%', height: '100%' },
            direction: 'right',
        });
    };

    useEffect(() => {
        // hasHydrated前(永続設定の読み込み前)にこの初期化を走らせると、defaultBoardが
        // まだ既定値(orange)のままの状態でinitialCode/initialBlockが確定してしまい、
        // 選択中ボード表示だけがhydration後の値(例: blue)に変わって、最初のファイル内容は
        // orangeのまま、というズレが生じる不具合があった。boardの反映とファイル初期化を
        // 同じhasHydrated後の1回にまとめることで解消する。
        if (!hasHydrated || initializedRef.current) return;
        initializedRef.current = true;

        const { files, setFiles } = useBoardEditorFileStore.getState();
        const cleanedFiles = files.filter((f) => !f.isNewFile);
        setFiles(cleanedFiles);

        const boardToUse = useBoardEditorConfigStore.getState().defaultBoard;
        useBoardEditorStore.getState().setBoard(boardToUse);

        const now = new Date();
        const timestamp =
            now.getFullYear().toString() +
            (now.getMonth() + 1).toString().padStart(2, '0') +
            now.getDate().toString().padStart(2, '0') +
            now.getHours().toString().padStart(2, '0') +
            now.getMinutes().toString().padStart(2, '0') +
            now.getSeconds().toString().padStart(2, '0') +
            now.getMilliseconds().toString().padStart(3, '0');

        const initialCode = getInitialCode(boardToUse);
        const initialBlock = getInitialXml(boardToUse);

        addFile({
            id: timestamp,
            name: `filename-${timestamp}`,
            codeContent: initialCode,
            lastSavedCodeContent: '',
            blockContent: initialBlock,
            lastSavedBlockContent: '',
            isDirty: true,
            isSaving: false,
            isSaved: false,
            isNewFile: true,
            lastModified: now.toISOString(),
            isBlockSynced: true,
            isCodeEditConfirmed: false,
            preEditBlockContent: '',
        });
        setCurrentFile(timestamp);
        setCode(initialCode);
    }, [addFile, setCurrentFile, setCode, hasHydrated]);

    useEffect(() => {
        const handler = (e: PromiseRejectionEvent) => {
            if (e.reason?.type === 'cancelation' && e.reason?.msg === 'operation is manually canceled') {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        };
        window.addEventListener('unhandledrejection', handler, { capture: true });
        return () => window.removeEventListener('unhandledrejection', handler, { capture: true });
    }, []);

    const clampTop = (height: number, px: number) => {
        const maxTop = Math.max(MIN_PANEL_HEIGHT, height - MIN_PANEL_HEIGHT);
        return Math.min(maxTop, Math.max(MIN_PANEL_HEIGHT, px));
    };

    const onMove = useCallback((e: PointerEvent) => {
        if (expandedRef.current) return;
        const el = containerRef.current;
        if (!el) return;
        const height = el.clientHeight;
        const nextTop = clampTop(height, startTop.current + (e.clientY - startY.current));
        setTopPx(nextTop);
        if (height > 0) {
            preferredRatioRef.current = nextTop / height;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onUp = useCallback(() => {
        setDragging(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
    }, [onMove]);

    useEffect(() => {
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [onMove, onUp]);

    const onHandleDown = (e: React.PointerEvent) => {
        if (expandedRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        startY.current = e.clientY;
        startTop.current = topPx;
        setDragging(true);
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp, { once: true });
    };

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const ro = new ResizeObserver(() => {
            if (expandedRef.current) return;
            const height = el.clientHeight;
            if (height <= 0) return;
            setTotalHeight(height);

            const desired = preferredRatioRef.current * height;
            const clamped = clampTop(height, desired);
            setTopPx(Math.round(clamped));
            if (desired === clamped) {
                preferredRatioRef.current = clamped / height;
            }
        });

        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const onToggleExpand = (e: React.MouseEvent | React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const next = !expanded;
        if (next) {
            setDragging(false);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        }
        setExpanded(next);
    };

    const expandedHeight = Math.max(MIN_PANEL_HEIGHT, totalHeight - SEPARATOR_HEIGHT);
    const transition: Transition = dragging
        ? { duration: DRAG_TRANSITION_DURATION }
        : { duration: EXPAND_TRANSITION_DURATION, ease: easeInOut };
    const separatorBgClass = isDark ? 'bg-color-ide-frame-dark-bg' : 'bg-color-ide-frame-bg';

    return (
        <div className="flex flex-col w-full h-full min-w-0 overflow-hidden rounded-b-md">
            <div className="relative flex w-full flex-none h-[70px] leading-none">
                {isUploading && <div className="absolute inset-0 z-[100] cursor-wait" />}
                <BoardToolbar />
            </div>

            <div className="flex w-full h-full min-w-0 overflow-hidden">
                <div className="flex-none">
                    <BoardEditorSidebar />
                </div>
                <div
                    ref={containerRef}
                    className={`relative flex flex-col flex-1 min-w-0 overflow-hidden ${dragging ? 'select-none' : ''}`}
                >
                    <motion.div
                        animate={{
                            height: expanded ? expandedHeight : clampTop(totalHeight, topPx),
                        }}
                        transition={transition}
                        className="relative min-h-[120px] overflow-hidden"
                    >
                        <div className="h-full flex flex-col">
                            <BoardEditorHeader onOpenFileList={handleOpenFileList} />
                            <div className="flex-1 overflow-hidden">
                                {isAIWhiteEnabled ? (
                                    <PanelGroup direction="horizontal" className="h-full">
                                        <Panel defaultSize={30} minSize={25}>
                                            <BoardAIAssistant />
                                        </Panel>
                                        <PanelResizeHandle
                                            className={`w-[3px] cursor-col-resize transition-colors ${
                                                isDark ? 'bg-color-ide-frame-dark-bg' : 'bg-color-ide-frame-bg'
                                            }`}
                                        />
                                        <Panel defaultSize={70} minSize={40}>
                                            <div className="h-full relative overflow-hidden">
                                                <div className={editorMode === 'block' ? 'absolute inset-0' : 'hidden'}>
                                                    <BoardBlockEditor />
                                                </div>
                                                <div className={editorMode === 'code' ? 'absolute inset-0' : 'hidden'}>
                                                    <div className="absolute inset-0">
                                                        <BoardCodeEditor />
                                                    </div>
                                                </div>
                                            </div>
                                        </Panel>
                                    </PanelGroup>
                                ) : (
                                    <div className="h-full relative overflow-hidden">
                                        <div className={editorMode === 'block' ? 'absolute inset-0' : 'hidden'}>
                                            <BoardBlockEditor />
                                        </div>
                                        <div className={editorMode === 'code' ? 'absolute inset-0' : 'hidden'}>
                                            <div className="absolute inset-0">
                                                <BoardCodeEditor />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>

                    <div
                        role="separator"
                        aria-orientation="horizontal"
                        className={`relative flex items-center justify-between ${separatorBgClass} z-20 select-none`}
                        style={{ height: SEPARATOR_HEIGHT }}
                    >
                        <div
                            onPointerDown={onHandleDown}
                            className="absolute inset-0 cursor-row-resize"
                            style={{ touchAction: 'none', pointerEvents: expanded ? 'none' : 'auto', zIndex: 1 }}
                        />

                        <div className="relative z-10 pointer-events-none flex-1">
                            <ProgressOverlay
                                isVisible={isUploading}
                                progress={uploadProgress}
                                status={uploadStatus}
                                message={uploadMessage}
                                queueAhead={uploadQueueAhead}
                                jobId={activeCompileJobId}
                            />
                        </div>

                        {!isUploading && (
                            <div className="relative z-10 flex items-center gap-2 px-3">
                                <button
                                    type="button"
                                    className={`p-1 rounded-lg transition-colors ${
                                        isDark
                                            ? 'text-black hover:bg-black/10'
                                            : 'text-color-white-base hover:bg-white/20'
                                    } ${logs.length === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                                    onClick={clearLogs}
                                    aria-label="Clear logs"
                                    disabled={logs.length === 0}
                                    title="ログをクリア"
                                >
                                    <IconAdaptive
                                        Icon={Trash}
                                        strokeWidth={2.0}
                                        baseSize={17}
                                        breakpoints={{ md: 17 }}
                                    />
                                </button>

                                <button
                                    type="button"
                                    className={`p-1 rounded-lg transition-colors ${
                                        isDark
                                            ? 'text-black hover:bg-black/10'
                                            : 'text-color-white-base hover:bg-white/20'
                                    }`}
                                    onPointerDown={onToggleExpand}
                                    onClick={onToggleExpand}
                                    aria-pressed={expanded}
                                    aria-label={expanded ? 'Collapse console' : 'Expand console'}
                                >
                                    <IconAdaptive
                                        Icon={expanded ? PanelTopClose : PanelTopOpen}
                                        strokeWidth={2.0}
                                        baseSize={17}
                                        breakpoints={{ md: 17 }}
                                    />
                                </button>
                            </div>
                        )}
                    </div>

                    <motion.div
                        animate={{
                            height: expanded
                                ? 0
                                : Math.max(0, totalHeight - (clampTop(totalHeight, topPx) + SEPARATOR_HEIGHT)),
                            opacity: expanded ? 0 : 1,
                        }}
                        transition={transition}
                        className="bg-black overflow-hidden shadow-inner"
                    >
                        <AnimatePresence>{!expanded && <BoardLogConsole />}</AnimatePresence>
                    </motion.div>

                    {dragging && (
                        <div className="fixed inset-0 z-50 cursor-row-resize" style={{ background: 'transparent' }} />
                    )}
                </div>
            </div>
        </div>
    );
}
